import type { TrackPayload } from "./types";
import { uid } from "./storage";

const CLICK_ID_PARAMS = ["clickid", "click_id", "cid", "subid", "binom_clickid"];
const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const;
const FLUSH_MS = 30000;

export function readClickId(params: URLSearchParams): string | undefined {
  for (const k of CLICK_ID_PARAMS) {
    const v = params.get(k);
    if (v) return v.slice(0, 128);
  }
  return undefined;
}

/**
 * Keeps one small record per page view and ships it in batches:
 * every 30s while something changed, and on page hide via sendBeacon — the
 * exit flush is what makes the numbers exact, so a longer tick costs nothing
 * but halves the requests a viewer makes.
 * The server upserts by session id, so repeated sends are idempotent.
 */
/** Ad-platform identifiers, read where the ad platforms leave them. */
function adIdentifiers(params: URLSearchParams): NonNullable<TrackPayload["ad"]> {
  const cookie = (name: string) => {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]).slice(0, 256) : undefined;
  };
  const fbclid = params.get("fbclid");
  const ad: NonNullable<TrackPayload["ad"]> = {
    fbp: cookie("_fbp"),
    // The pixel writes _fbc from fbclid, but an ad blocker stops it. Building the
    // same value from the URL is what keeps server-side matching working there.
    fbc: cookie("_fbc") || (fbclid ? `fb.1.${Date.now()}.${fbclid.slice(0, 200)}` : undefined),
    ttp: cookie("_ttp"),
    ttclid: params.get("ttclid")?.slice(0, 200) || cookie("ttclid"),
    url: location.href.slice(0, 500),
  };
  return ad;
}

export class Tracker {
  readonly sessionId = uid();
  private data: TrackPayload;
  private dirty = true;
  private timer: number | undefined;

  constructor(
    private url: string,
    playerId: string,
    videoId: string,
    duration: number,
    private enabled: boolean,
    clickIdOverride?: string,
  ) {
    const params = new URLSearchParams(location.search);
    const u: TrackPayload["u"] = {};
    for (const k of UTM_KEYS) {
      const v = params.get("utm_" + k);
      if (v) u[k] = v.slice(0, 128);
    }
    this.data = {
      s: this.sessionId,
      p: playerId,
      v: videoId,
      dur: Math.round(duration),
      sp: 0,
      mp: 0,
      ws: 0,
      ap: 0,
      st: 0,
      um: 0,
      oc: 0,
      kb: 0,
      ck: clickIdOverride || readClickId(params),
      r: document.referrer ? document.referrer.slice(0, 256) : undefined,
      tp: Math.min(32, navigator.maxTouchPoints || 0),
      u,
      ad: adIdentifiers(params),
    };
    if (!enabled) return;
    this.timer = window.setInterval(() => this.flush(false), FLUSH_MS);
    const onHide = () => this.flush(true);
    addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
    this.flush(false); // records the page load (impression)
  }

  get clickId(): string | undefined {
    return this.data.ck;
  }

  update(patch: Partial<TrackPayload>): void {
    for (const [k, v] of Object.entries(patch) as [keyof TrackPayload, never][]) {
      if (this.data[k] !== v) {
        this.data[k] = v;
        this.dirty = true;
      }
    }
  }

  /** Flags only ever go 0 -> 1, positions only grow. */
  position(pos: number): void {
    const p = Math.floor(pos);
    if (p > this.data.mp) this.update({ mp: p });
  }

  /** Traffic only rides along with other updates; it never triggers a request by itself. */
  setTraffic(kb: number): void {
    if (kb > this.data.kb) this.data.kb = Math.round(kb);
  }

  addWatched(sec: number): void {
    this.data.ws += sec;
    // watch time alone isn't worth a request; it rides along with the next flush
  }

  flush(final: boolean): void {
    if (!this.enabled || !this.dirty) return;
    this.dirty = false;
    const body = JSON.stringify({ ...this.data, ws: Math.round(this.data.ws) });
    // text/plain avoids a CORS preflight
    if (final && navigator.sendBeacon) {
      if (navigator.sendBeacon(this.url, new Blob([body], { type: "text/plain" }))) return;
    }
    fetch(this.url, {
      method: "POST",
      body,
      keepalive: true,
      headers: { "Content-Type": "text/plain" },
      credentials: "omit",
    }).catch(() => {
      this.dirty = true;
    });
  }

  destroy(): void {
    if (this.timer) clearInterval(this.timer);
    this.flush(true);
  }
}
